import React from "react";
import { Autocomplete, Box, Button, CircularProgress, InputAdornment, Stack, TextField } from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import useNaicsSectors from "../hooks/useNaicsSectors";
import { BRAND_BLUE, BRAND_GREEN } from "../../theme/colors";

function SectorDropdown({ label = "Sector", value, onChange, sx, options: _options,
  formatOptionLabel = null, helperText, SelectProps: _selectProps,
  disabled = false, name, onBlur, ...props }) {
  const { options, isPending, isError, refetch } = useNaicsSectors(value ? [value] : []);
  const optionLabel = (option) => formatOptionLabel ? formatOptionLabel(option) : option;

  return (
    <Stack spacing={0.5} sx={sx}>
      <Autocomplete
        fullWidth
        options={options}
        value={value || null}
        disabled={disabled}
        loading={isPending}
        autoHighlight
        openOnFocus
        getOptionLabel={optionLabel}
        onChange={(_event, selected) => onChange?.({ target: { name, value: selected || "" } })}
        onBlur={onBlur}
        clearText="Clear sector"
        openText="Browse sectors"
        closeText="Close sectors"
        loadingText="Loading sectors…"
        noOptionsText={isError ? "Sectors are unavailable. Please retry." : "No matching sectors. Try another search."}
        slotProps={{
          paper: {
            sx: {
              mt: 0.75, p: 0.5, borderRadius: 2.5,
              border: "1px solid rgba(0,51,108,0.12)",
              boxShadow: "0 12px 32px rgba(0,51,108,0.14)",
              color: BRAND_BLUE,
              "& .MuiAutocomplete-listbox": { maxHeight: 300, p: 0.5 },
              "& .MuiAutocomplete-option": {
                borderRadius: 1.5, minHeight: 42, px: 1.5, my: 0.25,
                "&.Mui-focused": { bgcolor: "rgba(0,51,108,0.06)" },
                '&[aria-selected="true"]': { bgcolor: "rgba(100,131,21,0.10)", fontWeight: 600 },
                '&[aria-selected="true"].Mui-focused': { bgcolor: "rgba(100,131,21,0.16)" },
              },
            },
          },
        }}
        renderOption={(optionProps, option, { selected }) => (
          <li {...optionProps}>
            <Box component="span" sx={{ flex: 1 }}>{optionLabel(option)}</Box>
            {selected && <CheckRoundedIcon sx={{ ml: 1, fontSize: 19, color: BRAND_GREEN }} />}
          </li>
        )}
        renderInput={(params) => (
          <TextField {...props} {...params} name={name} label={label}
            placeholder="Search sectors…"
            error={isError || props.error}
            helperText={isError ? "Could not load sectors. Your saved value is preserved." : helperText}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2, bgcolor: "background.paper",
                transition: "box-shadow 150ms ease",
                "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(0,51,108,0.08)" },
              },
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 20, color: "text.secondary" }} /></InputAdornment>,
              endAdornment: <>{isPending && <CircularProgress size={18} color="inherit" />}{params.InputProps.endAdornment}</>,
            }}
          />
        )}
      />
      {isError && <Button size="small" disabled={disabled} sx={{ alignSelf: "flex-start" }} onClick={() => refetch()}>Retry sectors</Button>}
    </Stack>
  );
}

export default SectorDropdown;
